import React, { useEffect } from 'react';
import { X, MapPin, Navigation } from 'lucide-react';
import GreenButton from './GreenButton';
import { BOOKING_URL, HOURS, MAPS_URL, fmtHour, ukStatus } from '../../lib/openingHours';

interface ShowroomModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ShowroomModal: React.FC<ShowroomModalProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const status = ukStatus();

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="showroom-modal-title"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Bottom sheet on phones, two-column card from sm up */}
      <div className="relative bg-white w-full sm:max-w-[820px] max-h-[92svh] sm:max-h-[calc(100svh-2rem)] rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col sm:grid sm:grid-cols-[2fr_3fr]">
        <button
          autoFocus
          onClick={onClose}
          className="absolute top-3 right-3 z-20 p-2 rounded-full bg-black/40 text-white hover:bg-black/60 sm:bg-transparent sm:text-gray-500 sm:hover:bg-gray-100 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Photo panel */}
        <div className="relative shrink-0 h-40 sm:h-auto overflow-hidden bg-black">
          <img src="/home/showroom-rail.jpg" alt="" className="absolute inset-0 w-full h-full object-cover object-[center_35%]" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/10" />
          <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7 text-white">
            <span
              className={`inline-flex items-center gap-2 rounded-full px-3 py-1 font-poppins text-[11px] font-semibold ${
                status.open ? 'bg-[#78BE20] text-white' : 'bg-white/15 text-white backdrop-blur'
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${status.open ? 'bg-white animate-pulse' : 'bg-white/60'}`} />
              {status.label}
            </span>
            <h2
              id="showroom-modal-title"
              className="font-montserrat font-extrabold tracking-[-0.03em] leading-[1.02] text-[28px] sm:text-[36px] mt-3"
            >
              Visit our
              <br className="hidden sm:block" /> <span className="text-[#78BE20]">Showroom</span>
            </h2>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto px-5 pt-5 pb-4 sm:px-7 sm:pt-8">
            <p className="font-poppins text-sm text-gray-600 leading-6 sm:pr-8">
              Pop down to browse over 200 garments, signage and print samples.
            </p>

            <h3 className="mt-5 font-poppins text-[10px] font-semibold tracking-[0.2em] uppercase text-gray-400">
              Opening hours
            </h3>
            <ul className="mt-2 font-poppins text-sm">
              {HOURS.map((h, i) => {
                const isToday = i === status.today;
                return (
                  <li
                    key={h.day}
                    className={`flex items-center justify-between py-1.5 px-3 -mx-3 border-l-2 ${
                      isToday ? 'border-[#78BE20] bg-[#78BE20]/10 font-semibold text-[#1a1a1a]' : 'border-transparent text-gray-600'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {h.day}
                      {isToday && (
                        <span className="text-[9px] font-semibold tracking-[0.15em] uppercase text-[#5a9418]">Today</span>
                      )}
                    </span>
                    <span className={`tabular-nums ${h.open ? '' : 'text-gray-400'}`}>
                      {h.open ? `${fmtHour(h.open[0])} – ${fmtHour(h.open[1])}` : 'Closed'}
                    </span>
                  </li>
                );
              })}
            </ul>

            <a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group mt-5 flex items-start gap-3 rounded-xl border border-gray-100 px-4 py-3 hover:border-[#78BE20] hover:bg-[#78BE20]/5 transition-colors"
            >
              <MapPin className="w-4 h-4 shrink-0 mt-0.5 text-[#6da71d]" strokeWidth={2} />
              <span className="min-w-0 flex-1">
                <span className="block font-poppins text-[13px] font-medium text-[#1a1a1a] leading-5">
                  23 (Unit 3) Lisle Avenue, Kidderminster, Worcestershire, DY11 7DF
                </span>
                <span className="block font-poppins text-xs text-gray-400 mt-1">
                  What3Words: <span className="font-medium text-gray-600">/// unity.rugs.firmly</span>
                </span>
              </span>
              <Navigation className="w-4 h-4 shrink-0 mt-0.5 text-gray-300 group-hover:text-[#6da71d] transition-colors" strokeWidth={2} />
            </a>

            <p className="mt-4 font-poppins text-xs leading-5 text-gray-500">
              Walk-ins are welcome, however we do advise booking a consultation to make sure one of our team is
              available to chat through options with you.
            </p>
          </div>

          {/* Actions stay in view while the details scroll */}
          <div className="shrink-0 border-t border-gray-100 px-5 py-4 sm:px-7 sm:pb-7 sm:border-t-0 grid grid-cols-1 gap-2">
            <GreenButton href={MAPS_URL} className="w-full justify-center">
              Get directions
            </GreenButton>
            <GreenButton variant="outline" href={BOOKING_URL} className="text-[#1a1a1a] w-full justify-center">
              Book a consultation
            </GreenButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShowroomModal;
